import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { reviewFeedbackKeys, reviewReportKeys, reviewTaskKeys } from "@/lib/query";
import type { ReviewFeedbackNoteInput } from "@/schemas/review-feedback";

/**
 * Written feedback on a review — EPIC-063.
 *
 * There is no hook for the unattributed comment, and that is deliberate: it is
 * never saved as it is typed. It lives in component state until the reviewer
 * hands in, and rides that request. An autosaved anonymous draft is a row on
 * the server that says who is writing it.
 */

export interface ReviewFeedbackNote {
  id: string;
  body: string;
  sentimentKey: string;
  sentimentName: string;
  promptKey: string | null;
  isAnonymous: boolean;
  isVisibleToEmployee: boolean;
  authorName: string | null;
  relationshipName: string | null;
  /** Which question this is about, if any — EPIC-072. */
  criterionId: string | null;
}

export interface FeedbackSentiment {
  key: string;
  name: string;
  description: string | null;
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const body = await res.json().catch(() => null);
  const message =
    body && typeof body === "object" && "error" in body && typeof body.error === "string"
      ? body.error
      : fallback;
  return new Error(message);
}

/** The tags a reviewer may choose. Never empty — the config list refuses to be. */
export function useFeedbackSentiments(enabled = true) {
  return useQuery({
    queryKey: reviewFeedbackKeys.sentiments,
    enabled,
    queryFn: async (): Promise<FeedbackSentiment[]> => {
      const res = await fetch("/api/review-feedback/sentiments");
      if (!res.ok) throw await failure(res, "Couldn't load the feedback types");
      return (await res.json()).data;
    },
  });
}

/** A reviewer's own notes on their own assignment. */
export function useMyReviewFeedback(reviewerId: string, enabled = true) {
  return useQuery({
    queryKey: reviewFeedbackKeys.mine(reviewerId),
    enabled,
    queryFn: async (): Promise<ReviewFeedbackNote[]> => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/feedback`);
      if (!res.ok) throw await failure(res, "Couldn't load your notes");
      return (await res.json()).data;
    },
  });
}

export function useAddReviewFeedback(reviewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: ReviewFeedbackNoteInput) => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't save that note");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewFeedbackKeys.mine(reviewerId) });
      toast.success("Note added");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useUpdateReviewFeedback(reviewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: ReviewFeedbackNoteInput & { id: string }) => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/feedback/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw await failure(res, "Couldn't save that note");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewFeedbackKeys.mine(reviewerId) });
      toast.success("Note saved");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

export function useDeleteReviewFeedback(reviewerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/review-tasks/${reviewerId}/feedback/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw await failure(res, "Couldn't remove that note");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewFeedbackKeys.mine(reviewerId) });
      toast.success("Note removed");
    },
    onError: (error: Error) => toast.error(error.message),
  });
}

/**
 * The manager's release decision.
 *
 * Invalidates the REPORT rather than the reviewer's notes: this is a change to
 * what the subject will be shown, and the report is where that is read.
 */
export function useSetFeedbackVisibility(reviewId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      feedbackId,
      isVisibleToEmployee,
    }: {
      feedbackId: string;
      isVisibleToEmployee: boolean;
    }) => {
      const res = await fetch(`/api/reviews/${reviewId}/feedback/${feedbackId}/visibility`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isVisibleToEmployee }),
      });
      if (!res.ok) throw await failure(res, "Couldn't change who sees this");
      return res.json();
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewReportKeys.detail(reviewId) });
      queryClient.invalidateQueries({ queryKey: reviewTaskKeys.all });
      toast.success(
        variables.isVisibleToEmployee ? "Shared with them" : "No longer shared with them",
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });
}
